import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { fetchReports, type ReportsPeriod, type ReportsSnapshot } from '@/lib/api';

// 2026-06-30 (DateFilter) — the key includes the custom from/to so each
// resolved range caches independently (different ranges must not collide).
export function reportsQueryKey(slug: string, period: ReportsPeriod, from?: string, to?: string) {
  return ['reports', slug, period, from ?? '', to ?? ''] as const;
}

/**
 * useReports — pulls the GET /pwa/reports snapshot for the requested range.
 * Period (+ custom from/to) are function args (synced to the URL by the
 * Reports view) so each selection creates its own cache entry — switching
 * back to a previously viewed range is instant.
 */
export function useReports(
  slug: string,
  period: ReportsPeriod,
  from?: string,
  to?: string,
): UseQueryResult<ReportsSnapshot> {
  const isCustom = period === 'custom';
  return useQuery<ReportsSnapshot>({
    queryKey: reportsQueryKey(slug, period, from, to),
    queryFn: () => fetchReports({ slug, period, from, to }),
    // A custom selection without both dates never fires (the picker only
    // applies complete ranges, but belt-and-suspenders).
    enabled: Boolean(slug) && (!isCustom || Boolean(from && to)),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}
